import { FunctionComponent, useContext, useMemo } from 'react'
import styled, { DefaultTheme, ThemeContext } from 'styled-components'

import Container from 'components/GlobalStyles/Marketing/Container'
import Text from 'components/GlobalStyles/Text'
import RoundedButton from 'components/RoundedButton'
import BrowserTab from 'components/SVG/BrowserTab'
import Checklist from 'components/SVG/Checklist'
import FlaredArrow from 'components/SVG/FlaredArrow'
import Headphone from 'components/SVG/Headphone'
import Spacer from 'components/Spacer'

import { useWindowSize } from 'services/hooks'

const getImageUrl = (theme: DefaultTheme, width: number | undefined) => {
  if (!width) return undefined

  if (width && width < theme.metrics.tablet) {
    return `${process.env.CDN_BASE_URL}/q_auto,c_fill,ar_16:9,c_crop/amazon/assets/couple-browsing-internet-v2.jpg`
  }

  if (width && width < theme.metrics.desktop) {
    return `${process.env.CDN_BASE_URL}/q_auto,c_fill,w_1440/amazon/assets/couple-browsing-internet-v2.jpg`
  }

  return `${process.env.CDN_BASE_URL}/q_auto,c_fill,w_1920/amazon/assets/couple-browsing-internet-v2.jpg`
}

const Header = styled.h3`
  font-size: 36px;
  font-weight: 800;
  text-align: center;
  margin: 0;
  color: ${props => props.theme.colors.grey27};
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 44px;
  }
`
interface IImageUrlProps {
  imgSrc: string
}

const BackgroundImage = styled.div<IImageUrlProps>`
  background: top / contain url(${props => props.imgSrc}) no-repeat;
  width: 100%;
  height: 100%;
`

const ContentWrapperCard = styled.div`
  background-color: ${props => props.theme.colors.white};
  border-radius: 24px;
  box-shadow: none;
  padding: 16px;
  box-shadow: 0px 12px 48px ${props => props.theme.colors.transparentGrey23Alpha15};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    padding: 24px;
  }
  @media (min-width: ${props => props.theme.metrics.desktopLarge}px) {
    padding: 32px;
  }
`

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    flex-direction: row;
  }
`

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const Card = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 15px 24px;
  background: ${props => props.theme.colors.yellow4};
  border-radius: 25px;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    max-width: 350px;
    height: 102px;
  }
`

const SavingsCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    flex-direction: row;
  }
`

const Emphasis = styled.span`
  font-size: 36px;
  font-weight: 900;
  color: ${props => props.theme.colors.blue};
  text-align: center;
  padding-bottom: 4px; // font correction
  line-height: 46px;
`

const Icon = styled.div`
  width: 68px;
  height: 68px;
`

const Label = styled.div`
  font-size: 18px;
  text-align: left;
  color: ${props => props.theme.colors.white};
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 20px;
    text-align: center;
  }
`

interface IProps {
  scrollTo?: () => void
}

const ExplainerA: FunctionComponent<IProps> = ({ scrollTo }) => {
  const theme = useContext(ThemeContext)

  const renderRightArrowIcon = () => (
    <FlaredArrow
      fill={theme.colors.white}
      width={15}
      height={15}
    />
  )

  const { width } = useWindowSize()
  const backgroundImgSrc = useMemo(() => getImageUrl(theme, width), [width])

  return (
    <BackgroundImage imgSrc={backgroundImgSrc || ''}>
      <Container>
        <Spacer
          size={250}
          tabletSize={180}
          responsiveSize={150}
        />
        <ContentWrapperCard>
          <Header>Consider it sold.</Header>
          <Spacer size={32} />
          <Text
            color={theme.colors.charcoal}
            fontSize={16}
            textAlign="center"
            lineHeight="22px"
            paragraph
          >
            For decades, home sellers have been forced to choose between hiring an overpriced real
            estate agent to get their home the necessary exposure to get top dollar or selling it on
            their own without being on the MLS. We decided it was time to change that. Enter
            HomeLister, an easy, stress-free way to get your house on the MLS and sold without all
            the hefty, percentage-based commissions. We provide as much (or as little) help as you
            want - from professional photography and staging solutions to offer negotiation and
            digital transaction management from seasoned real estate experts. We&apos;ve got your
            back every step of the way.
          </Text>
          <Spacer size={32} />
          <Text
            color={theme.colors.grey23}
            fontSize={20}
            fontWeight={800}
            textAlign="center"
            paragraph
          >
            It&apos;s your home, it&apos;s your equity, it&apos;s your sale. Own it!
          </Text>
          <Spacer size={32} />
          <CardsContainer>
            <CardWrapper>
              <Card>
                <Icon>
                  <Checklist
                    fill={theme.colors.white}
                    width={68}
                    height={68}
                  />
                </Icon>
                <Spacer size={12} />
                <Label>Low Flat Fee to List, Pay at Close</Label>
              </Card>
              <Spacer size={5} />
            </CardWrapper>
            <Spacer size={16} />
            <Card>
              <Icon>
                <BrowserTab
                  fill={theme.colors.white}
                  width={68}
                  height={68}
                />
              </Icon>
              <Spacer size={12} />
              <Label>Property Listed on MLS & Major Real Estate Sites</Label>
            </Card>
            <Spacer size={16} />
            <Card>
              <Icon>
                <Headphone
                  fill={theme.colors.white}
                  width={68}
                  height={68}
                />
              </Icon>
              <Spacer size={12} />
              <Label>Professional Help at Anytime</Label>
            </Card>
          </CardsContainer>
          <Spacer size={36} />
          <SavingsCard>
            <Text
              color={theme.colors.grey23}
              fontSize={24}
              textAlign="center"
            >
              HomeLister sellers &nbsp;
            </Text>
            <Emphasis>save an average of $21,000</Emphasis>
            <Text
              color={theme.colors.grey23}
              fontSize={24}
              textAlign="center"
            >
              &nbsp;on commission fees!
            </Text>
          </SavingsCard>
        </ContentWrapperCard>
        <Spacer
          size={50}
          tabletSize={40}
          responsiveSize={30}
        />
        <RoundedButton
          text="Get Started"
          background={theme.colors.blue4}
          hoverBackground={theme.colors.blue9}
          color={theme.colors.white}
          boxShadow={`0px 12px 16px ${theme.colors.transparentBlue4Alpha25}`}
          renderRightIcon={renderRightArrowIcon}
          fontSize={12}
          radius={50}
          height={44}
          width={240}
          center
          onClick={scrollTo}
        />
        <Spacer
          size={70}
          tabletSize={50}
          responsiveSize={30}
        />
      </Container>
    </BackgroundImage>
  )
}

export default ExplainerA
